// The HTML-instead-of-JSON guard, in one place (#317 A73, ADR 0026).
//
// WHAT IT PREVENTS. When a request is misrouted — a wrong `VITE_*` base URL, a
// proxy rule that fell through, an ingress serving the SPA shell for an `/api`
// path — the response is a 200 carrying an HTML error page. Axios hands that to
// `JSON.parse`, which throws a SyntaxError from inside the transform; some
// callers swallow it and the app reports a benign empty state. Worse, a copy of
// this transform that dropped the check made an HTML error page resolve as a
// SUCCESSFUL response, which is recorded in the original in-repo comment: a
// misconfigured deploy looked healthy from the browser.
//
// It existed in four of the eight UIs with an axios client and not the other
// four, and only one copy logged. Being in app-kit means "which apps have it" is
// no longer a question anyone has to answer per app.
//
// Two halves, and BOTH are needed:
//   1. `parseJsonOrFlagHtml` as the instance's `transformResponse` — turns the
//      HTML body into a flag object instead of throwing mid-parse.
//   2. `rejectHtmlResponse` as a response interceptor — turns that flag into a
//      rejected promise, so it reaches the caller's catch rather than being read
//      as data. A client that installs only the transform reports the flag
//      object as the response payload, which is its own quiet failure.

/** Marker put on the parsed body when the response was an HTML page. */
export const HTML_RESPONSE_MARKER = 'RECEIVED_HTML_RESPONSE';

type HtmlFlag = { _apiError: typeof HTML_RESPONSE_MARKER; content: string };

/**
 * Axios `transformResponse` entry. Parses JSON, and flags an HTML body rather
 * than letting `JSON.parse` throw. A body that is neither is returned as-is —
 * a plain-text 500 from a proxy is still worth showing.
 */
export function parseJsonOrFlagHtml(data: unknown): unknown {
  if (typeof data !== 'string') return data;
  const trimmed = data.trim();
  if (trimmed.startsWith('<') && (trimmed.includes('<html') || trimmed.includes('<!DOCTYPE'))) {
    // Logged, not silent: the cause is always configuration, and the first 200
    // characters usually name the server that answered.
    console.error('[api] received HTML instead of JSON — check the API base URL / proxy:', trimmed.slice(0, 200));
    return { _apiError: HTML_RESPONSE_MARKER, content: trimmed.slice(0, 200) } satisfies HtmlFlag;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return data;
  }
}

/**
 * Axios response interceptor (the fulfilled side). Rejects a flagged HTML
 * response so it lands in the caller's error path.
 */
export function rejectHtmlResponse<T extends { data?: unknown }>(response: T): T | Promise<never> {
  const data = response?.data as HtmlFlag | undefined;
  if (data && typeof data === 'object' && (data as HtmlFlag)._apiError === HTML_RESPONSE_MARKER) {
    const err = new Error('API returned HTML instead of JSON. Check the API base URL / proxy config.');
    (err as Error & { response?: unknown }).response = response;
    return Promise.reject(err);
  }
  return response;
}

/**
 * Installs both halves on an axios instance. Use this rather than wiring the two
 * separately — installing only one is the failure mode this guard exists for.
 *
 * Idempotent per instance: calling it twice does not double-register.
 */
export function installHtmlResponseGuard(instance: {
  defaults: { transformResponse?: unknown };
  interceptors: { response: { use: (onFulfilled: (r: any) => any) => number } };
}): void {
  const marked = instance as { __htmlGuardInstalled?: boolean };
  if (marked.__htmlGuardInstalled) return;
  marked.__htmlGuardInstalled = true;
  // Replaces axios's default JSON transform outright: it is the same parse plus
  // the HTML check, so chaining them would double-parse.
  instance.defaults.transformResponse = [parseJsonOrFlagHtml];
  instance.interceptors.response.use(rejectHtmlResponse);
}
